/**
 * Local-only achievement system. Tracks milestones in localStorage and fires a
 * callback when a new one unlocks. State is per-browser; the server doesn't
 * know about achievements.
 */

interface Achievement {
	id: string;
	title: string;
	hint: string;
	check: (s: AchievementContext) => boolean;
}

export interface AchievementContext {
	balanceCents: number;
	completedContracts: number;
	totalPaidCents: number;
	repairCount: number;
	printersOwned: number;
	workersHired: number;
	racksOwned: number;
	maxClientRepLevel: number;
	uniqueClientsServed: number;
}

const ACHIEVEMENTS: Achievement[] = [
	// Contracts
	{ id: "first-payout", title: "First Payout", hint: "Completed your first contract", check: (s) => s.completedContracts >= 1 },
	{ id: "five-contracts", title: "Steady Operator", hint: "Completed 5 contracts", check: (s) => s.completedContracts >= 5 },
	{ id: "25-contracts", title: "Industrial Veteran", hint: "Completed 25 contracts", check: (s) => s.completedContracts >= 25 },
	{ id: "100-contracts", title: "Centurion", hint: "Completed 100 contracts", check: (s) => s.completedContracts >= 100 },
	{ id: "500-contracts", title: "Production Maestro", hint: "Completed 500 contracts", check: (s) => s.completedContracts >= 500 },

	// Money
	{ id: "1k-balance", title: "First Grand", hint: "Saved $1,000", check: (s) => s.balanceCents >= 100000 },
	{ id: "10k-balance", title: "Five Figures", hint: "Saved $10,000", check: (s) => s.balanceCents >= 1000000 },
	{ id: "100k-balance", title: "Six Figures", hint: "Saved $100,000", check: (s) => s.balanceCents >= 10000000 },
	{ id: "1m-balance", title: "Seven Figures", hint: "Saved $1,000,000", check: (s) => s.balanceCents >= 100000000 },
	{ id: "10k-revenue", title: "Steady Cashflow", hint: "Earned $10K total", check: (s) => s.totalPaidCents >= 1000000 },
	{ id: "100k-revenue", title: "Quarter-million handler", hint: "Earned $100K total", check: (s) => s.totalPaidCents >= 10000000 },

	// Repairs
	{ id: "first-repair", title: "Wrench Hand", hint: "Repaired a failed printer", check: (s) => s.repairCount >= 1 },
	{ id: "ten-repairs", title: "Field Tech", hint: "Repaired 10 failed printers", check: (s) => s.repairCount >= 10 },
	{ id: "fifty-repairs", title: "Senior Service Engineer", hint: "Repaired 50 failed printers", check: (s) => s.repairCount >= 50 },

	// Fleet
	{ id: "first-second-printer", title: "Expansion", hint: "Owned a second printer", check: (s) => s.printersOwned >= 2 },
	{ id: "four-printers", title: "Full Workshop", hint: "Filled all 4 starter slots", check: (s) => s.printersOwned >= 4 },
	{ id: "eight-printers", title: "Print Farm", hint: "Operated 8 printers", check: (s) => s.printersOwned >= 8 },
	{ id: "twelve-printers", title: "Production Floor", hint: "Operated 12 printers", check: (s) => s.printersOwned >= 12 },
	{ id: "rack-buy-first", title: "More Capacity", hint: "Bought your first extra rack", check: (s) => s.racksOwned >= 3 },
	{ id: "rack-buy-max", title: "Wall-to-Wall", hint: "Filled the warehouse with 4 racks", check: (s) => s.racksOwned >= 4 },

	// Workforce
	{ id: "first-worker", title: "First Hire", hint: "Hired your first worker", check: (s) => s.workersHired >= 1 },
	{ id: "three-workers", title: "Small Crew", hint: "Built a 3-person crew", check: (s) => s.workersHired >= 3 },
	{ id: "ten-workers", title: "Full Shop Floor", hint: "Hired 10 workers", check: (s) => s.workersHired >= 10 },

	// Reputation
	{ id: "rep-level-3", title: "Trusted Vendor", hint: "Hit rep level 3 with any client", check: (s) => s.maxClientRepLevel >= 3 },
	{ id: "rep-level-5", title: "Preferred Supplier", hint: "Hit max rep with a client", check: (s) => s.maxClientRepLevel >= 5 },
	{ id: "five-clients", title: "Reaching Out", hint: "Served 5 different clients", check: (s) => s.uniqueClientsServed >= 5 },
	{ id: "ten-clients", title: "Local Favorite", hint: "Served 10 different clients", check: (s) => s.uniqueClientsServed >= 10 },
	{ id: "all-utah-clients", title: "The Utah Network", hint: "Served every client at least once", check: (s) => s.uniqueClientsServed >= 21 },
];

const STORAGE_KEY = "utah-achievements-v1";
const STATS_KEY = "utah-stats-v1";

function loadUnlocked(): Set<string> {
	try {
		return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as string[]);
	} catch {
		return new Set();
	}
}

function saveUnlocked(set: Set<string>) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
	} catch {
		/* localStorage disabled */
	}
}

interface PersistedStats {
	completedContracts: number;
	totalPaidCents: number;
	repairCount: number;
}

export function loadStats(): PersistedStats {
	try {
		return JSON.parse(localStorage.getItem(STATS_KEY) ?? '{"completedContracts":0,"totalPaidCents":0,"repairCount":0}') as PersistedStats;
	} catch {
		return { completedContracts: 0, totalPaidCents: 0, repairCount: 0 };
	}
}

export function saveStats(stats: PersistedStats) {
	try {
		localStorage.setItem(STATS_KEY, JSON.stringify(stats));
	} catch {
		/* ignore */
	}
}

const unlocked = loadUnlocked();

export function checkAchievements(ctx: AchievementContext, onUnlock: (a: Achievement) => void) {
	for (const a of ACHIEVEMENTS) {
		if (unlocked.has(a.id)) continue;
		if (a.check(ctx)) {
			unlocked.add(a.id);
			saveUnlocked(unlocked);
			onUnlock(a);
		}
	}
}
